import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Tab,
    Tabs,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    useTheme,
    alpha,
    Badge
} from '@mui/material';
import {
    Lightbulb,
    Category,
    Refresh,
    School,
    HelpOutline,
    AllInclusive
} from '@mui/icons-material';
import { getAllTips, getTipsByCategory, getRandomTip } from '../api/tips';
import { getAllCategories } from '../api/categories';

const TipsPage = () => {
    const theme = useTheme();
    const [tips, setTips] = useState([]);
    const [allTips, setAllTips] = useState([]); // Store all tips for filtering
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(0); // 0 = All
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [randomTip, setRandomTip] = useState(null);
    const [tipCounts, setTipCounts] = useState({}); // Count tips per category

    useEffect(() => {
        fetchCategories();
        fetchAllTips();
        fetchRandomTip();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await getAllCategories();
            setCategories(response.data || []);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };

    const fetchAllTips = async () => {
        setLoading(true);
        try {
            const response = await getAllTips();
            const allTipsData = response.data || [];
            setAllTips(allTipsData);
            setTips(allTipsData); // Initially show all tips

            // Calculate tip counts for each category
            calculateTipCounts(allTipsData);
        } catch (err) {
            setError('Failed to load educational tips');
            console.error('Error fetching tips:', err);
        } finally {
            setLoading(false);
        }
    };

    const calculateTipCounts = (tipsData) => {
        const counts = {
            all: tipsData.length,
            uncategorized: tipsData.filter(tip => !tip.category).length
        };

        // Count tips for each category
        categories.forEach(category => {
            counts[category.id] = tipsData.filter(tip => tip.category?.id === category.id).length;
        });

        setTipCounts(counts);
    };

    const fetchRandomTip = async () => {
        try {
            const response = await getRandomTip();
            setRandomTip(response.data);
        } catch (err) {
            console.error('Failed to fetch random tip:', err);
        }
    };

    const handleCategoryChange = (event, newValue) => {
        setSelectedCategory(newValue);

        if (newValue === 0) {
            // All tips
            setTips(allTips);
        } else if (newValue === 1) {
            // Uncategorized tips
            const uncategorizedTips = allTips.filter(tip => !tip.category);
            setTips(uncategorizedTips);
        } else {
            // Specific category
            const categoryId = categories[newValue - 2]?.id; // Adjust index for All and Uncategorized tabs
            const categoryTips = allTips.filter(tip => tip.category?.id === categoryId);
            setTips(categoryTips);
        }
    };

    const getCategoryColor = (categoryName) => {
        const colors = {
            'Compost': '#4caf50',
            'Recycle': '#2196f3',
            'Hazardous': '#f44336',
            'General_Waste': '#9e9e9e',
            'E-Waste': '#ff9800',
            'Unknown': '#9c27b0'
        };
        return colors[categoryName] || theme.palette.primary.main;
    };

    const getTabIcon = (tabType, categoryName) => {
        if (tabType === 'all') return <AllInclusive />;
        if (tabType === 'uncategorized') return <HelpOutline />;

        // Category-specific icons
        const icons = {
            'Compost': '🌱',
            'Recycle': '♻️',
            'Hazardous': '⚠️',
            'General_Waste': '🗑️',
            'E-Waste': '💻',
            'Unknown': '❓'
        };
        return icons[categoryName] || <Category />;
    };

    // Update tip counts when categories change
    useEffect(() => {
        if (allTips.length > 0 && categories.length > 0) {
            calculateTipCounts(allTips);
        }
    }, [categories, allTips]);

    return (
        <Box sx={{ minHeight: '100vh', py: 4 }}>
            <Container maxWidth="lg">
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                        variant="h2"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 2
                        }}
                    >
                        <School sx={{ fontSize: '0.8em', mr: 1, verticalAlign: 'middle' }} />
                        Educational Tips
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                        Learn how to properly sort waste and contribute to a cleaner environment
                    </Typography>
                </Box>

                {/* Random Tip Spotlight */}
                {randomTip && (
                    <Card
                        sx={{
                            mb: 4,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Lightbulb sx={{ color: theme.palette.secondary.main, mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        💡 Tip of the Day
                                    </Typography>
                                </Box>
                                <Tooltip title="Get another random tip">
                                    <IconButton onClick={fetchRandomTip} color="primary">
                                        <Refresh />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Grid container spacing={2}>
                                {randomTip.imageUrl && (
                                    <Grid item xs={12} md={3}>
                                        <CardMedia
                                            component="img"
                                            image={randomTip.imageUrl}
                                            alt={randomTip.title || 'Educational tip'}
                                            sx={{
                                                height: 120,
                                                borderRadius: 2,
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} md={randomTip.imageUrl ? 9 : 12}>
                                    {randomTip.title && (
                                        <Typography variant="h6" sx={{ mb: 1, color: theme.palette.primary.main }}>
                                            {randomTip.title}
                                        </Typography>
                                    )}
                                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                                        {randomTip.tipText}
                                    </Typography>
                                    {randomTip.category ? (
                                        <Chip
                                            label={randomTip.category.name}
                                            size="small"
                                            sx={{
                                                background: alpha(getCategoryColor(randomTip.category.name), 0.1),
                                                color: getCategoryColor(randomTip.category.name),
                                                fontWeight: 600
                                            }}
                                        />
                                    ) : (
                                        <Chip
                                            label="General Tips"
                                            size="small"
                                            sx={{
                                                background: alpha(theme.palette.grey[500], 0.1),
                                                color: theme.palette.grey[700],
                                                fontWeight: 600
                                            }}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Enhanced Category Tabs */}
                <Box sx={{ mb: 4 }}>
                    <Tabs
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                minWidth: 120,
                                fontWeight: 600,
                                textTransform: 'none'
                            }
                        }}
                    >
                        {/* All Tips Tab */}
                        <Tab
                            label={
                                <Badge badgeContent={tipCounts.all || 0} color="primary" sx={{ pr: 1 }}>
                                    All Tips
                                </Badge>
                            }
                            icon={getTabIcon('all')}
                            iconPosition="start"
                        />

                        {/* Uncategorized Tips Tab */}
                        {tipCounts.uncategorized > 0 && (
                            <Tab
                                label={
                                    <Badge badgeContent={tipCounts.uncategorized} color="secondary" sx={{ pr: 1 }}>
                                        General Tips
                                    </Badge>
                                }
                                icon={getTabIcon('uncategorized')}
                                iconPosition="start"
                            />
                        )}

                        {/* Category Tabs */}
                        {categories.map((category) => (
                            <Tab
                                key={category.id}
                                label={
                                    <Badge badgeContent={tipCounts[category.id] || 0} color="primary" sx={{ pr: 1 }}>
                                        {category.name}
                                    </Badge>
                                }
                                icon={getTabIcon('category', category.name)}
                                iconPosition="start"
                            />
                        ))}
                    </Tabs>
                </Box>

                {/* Tips Grid */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={60} />
                    </Box>
                ) : (
                    <>
                        {tips.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary">
                                    No educational tips found for this category.
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                {tips.map((tip) => (
                                    <Grid item xs={12} md={6} lg={4} key={tip.id}>
                                        <Card
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: theme.shadows[8]
                                                }
                                            }}
                                        >
                                            {tip.imageUrl && (
                                                <CardMedia
                                                    component="img"
                                                    height="200"
                                                    image={tip.imageUrl}
                                                    alt={tip.title || 'Educational tip'}
                                                    sx={{ objectFit: 'cover' }}
                                                />
                                            )}
                                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                                {tip.title && (
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            mb: 2,
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {tip.title}
                                                    </Typography>
                                                )}
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2, lineHeight: 1.6 }}
                                                >
                                                    {tip.tipText}
                                                </Typography>
                                                {tip.category ? (
                                                    <Chip
                                                        label={tip.category.name}
                                                        size="small"
                                                        sx={{
                                                            background: alpha(getCategoryColor(tip.category.name), 0.1),
                                                            color: getCategoryColor(tip.category.name),
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="General Tips"
                                                        size="small"
                                                        sx={{
                                                            background: alpha(theme.palette.grey[500], 0.1),
                                                            color: theme.palette.grey[700],
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
};

export default TipsPage;
